import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { App } from '@df/core/app';
import { IZippyChangeEvent, IZippyEvent, ZippyEventType, subject } from '@df/legacy/ui/zippy/zippy.abstract';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { AbIfService } from '@df/module-ab/ab-if.service';
import { AbModule } from '@df/module-ab/ab.module';
import { Ga4TrackClickDirective } from '@df/module-gtm/ga4-track-click.directive';
import { ImageModule } from '@df/module-image/image.module';
import { IfLoggedInDirective } from '@df/session/customer/if-logged-in.directive';
import { IfNotLoggedInDirective } from '@df/session/customer/if-not-logged-in.directive';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { PAGE_MENU_TAB_PREFERENCE_FIELD } from '../../../catalog/page/page.config';
import { GtmTrackNavigationClickDirective } from '../../../module/gtm/gtm-track-navigation-click.directive';
import { OffCanvasSearchComponent } from '../../../search/ui/off-canvas-search.component';
import { IMenuTab, addMenuTabs } from '../../add-menu-tabs';
import { OFF_CANVASS_MENU_ZIPPY_CLOSE_EVENT } from '../off-canvas-menu.component';
import { OFF_CANVAS_BLOCK_CONFIG, OFF_CANVAS_BLOCK_ID, OFF_CANVAS_MENU_FIELD } from './off-canvas-menu-body.config';

export interface IMenuBodyBlockContent {
    menuTabs: IMenuTab<IOffCanvasMenuItem>[];
    footerLinks: IOffCanvasMenuBodyFooterLinkItem[];
}
interface IOffCanvasMenuItem {
    text: string;
    link?: string;
    color?: string;
    secondLevelTurnOn?: boolean;
    secondLevelItems: IOffCanvasMenuBodySecondLevelItem[];
}
export interface IOffCanvasMenuBodySecondLevelItem {
    text: string;
    link?: string;
    color?: string;
    thirdLevelTurnOn?: boolean;
    thirdLevelItems: IOffCanvasMenuBodyThirdLevelItem[];
    featuredCarouseTurnOn?: string;
    featuredHeader?: string;
    featuredCarousel: IOffCanvasMenuBodyFeaturedCarouselItem[];
}
export interface IOffCanvasMenuBodyThirdLevelItem {
    text: string;
    link?: string;
    color?: string;
}
export interface IOffCanvasMenuBodyFooterLinkItem {
    icons: 'icon-account' | 'icon-phone' | 'icon-information' | 'icon-help' | 'icon-location';
    text: string;
    link?: string;
}
export interface IOffCanvasMenuBodyFeaturedCarouselItem {
    image?: string;
    text?: string;
    link?: string;
}

addMenuTabs(OFF_CANVAS_BLOCK_CONFIG, OFF_CANVAS_MENU_FIELD);

let uid = 0;
@Component({
    selector: 'off-canvas-menu-body',
    templateUrl: 'off-canvas-menu-body.html',
    styleUrls: ['./off-canvas-menu-body.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        HrefComponent,
        GtmTrackNavigationClickDirective,
        TranslationModule,
        ZippyModule,
        ICmsClientModule,
        ImageModule,
        CarouselModule,
        Ga4TrackClickDirective,
        OffCanvasSearchComponent,
        CommonModule,
        AbModule,
        IfLoggedInDirective,
        IfNotLoggedInDirective,
        RouterLink
    ]
})
@ICmsComponent<OffCanvasMenuBodyComponent>(OFF_CANVAS_BLOCK_CONFIG)
export class OffCanvasMenuBodyComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnChanges {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: IMenuBodyBlockContent;

    zippyCloseEventName = OFF_CANVASS_MENU_ZIPPY_CLOSE_EVENT;

    @Input()
    isVisible = false;

    @Output()
    hide = new EventEmitter<MethodDecorator>();

    private _activeMenuTab?: IMenuTab<IOffCanvasMenuItem>;

    app = inject(App);

    abIfService = inject(AbIfService);

    name = OFF_CANVAS_BLOCK_ID;

    /**
     * If meganav was ever visible
     */
    wasEverVisibile = false;

    /**
     * Meganav items that were ever visible
     */
    private everVisibleItemsIndex: {
        [key: string]: boolean;
    } = {};

    readonly uid = `offCanvasMenu.${uid++}`;

    override ngOnInit(): void {
        if (this.client.isPrerender) {
            this.wasEverVisibile = true;
        }

        this.subscriptions.add(subject.subscribe((event: IZippyEvent) => this.onEvent(event)));

        super.ngOnInit();
        this.subscriptions.add(this.app.meta.subscribe(() => this.onMetaModelUpdate()));
        this.onMetaModelUpdate();
    }
    override ngOnChanges(changes: SimpleChanges): void {
        super.ngOnChanges(changes);

        if (changes['isVisible']) {
            this.detectChanges();
        }
    }
    hideOffCanvas(): void {
        this.hide.emit();
    }
    private onMetaModelUpdate(): void {
        const model = this.app.meta.model;
        if (model?.data && model.type === 'page') {
            if (model.data?.['additional']) {
                const menuTabPreferenceId = model.data['additional'][PAGE_MENU_TAB_PREFERENCE_FIELD.key];
                if (menuTabPreferenceId) {
                    const preferenceTabItem = this.menuTabs.find(i => i.id === menuTabPreferenceId);
                    if (preferenceTabItem) {
                        this.activeMenuTab = preferenceTabItem;
                    }
                }
            }
        }
    }
    protected onModelChange(): void {
        this.onMetaModelUpdate();
    }
    get menuTabs(): IMenuTab<IOffCanvasMenuItem>[] {
        return this.content?.menuTabs || [];
    }

    get tabs(): IMenuTab<IOffCanvasMenuItem>[] {
        return this.client.isBot || this.client.isPrerender ? this.menuTabs : [this.activeMenuTab];
    }

    get activeMenuTab(): IMenuTab<IOffCanvasMenuItem> {
        return (
            this._activeMenuTab ||
            this.menuTabs.find(tab => !tab.abIfExp || this.abIfService.getState(tab.abIfExp).value) ||
            this.menuTabs[0]
        );
    }
    set activeMenuTab(tab: IMenuTab<IOffCanvasMenuItem>) {
        this._activeMenuTab = tab;
        this.detectChanges();
    }
    get menuItems(): IOffCanvasMenuItem[] {
        return this.activeMenuTab?.menu || [];
    }

    protected onEvent(event: IZippyEvent | IZippyChangeEvent): void {
        if (!event.stopped) {
            if (event.type === ZippyEventType.open || event.type === ZippyEventType.toggle) {
                if (event.id?.group === this.name) {
                    const change = !this.wasEverVisibile || !this.everVisibleItemsIndex[event.id.name];

                    if (change) {
                        this.wasEverVisibile = true;
                        this.everVisibleItemsIndex[event.id.name] = true;
                        this.changeDetectorRef.detectChanges();
                    }
                }
            }
        }
    }

    getZippyId(index: number | string) {
        return [this.name, index];
    }

    /**
     * Checks if menu item can be printed (has items or cms)
     */
    canPrintContent(item): boolean {
        return !!(this.client.isCms || item?.length);
    }

    /**
     * Check if menu item body can be printed (if was requested already)
     */
    canPrintContentBody(index: number | string): boolean {
        if (this.client.isPrerender) {
            return true;
        }

        if (this.client.isCms) {
            return true;
        }

        return this.everVisibleItemsIndex[index];
    }
}
